import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import PageBanner from './PageBanner';
import FeaturedProducts from '../FeaturedProducts';

const GamesSection = styled.section`
  padding: 1rem 2rem 3rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const PlayFeaturedCard = styled(motion.div)`
  background: white;
  border-radius: 24px;
  padding: 3rem 2rem;
  text-align: center;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
  border: 4px solid #6bcf7f;
  margin-bottom: 2.5rem;

  .big-icon {
    font-size: 3.5rem;
    margin-bottom: 0.5rem;
  }

  h2 {
    color: #6bcf7f;
    font-size: 2.3rem;
    margin: 0 0 1rem 0;
  }

  p {
    font-size: 1.15rem;
    color: #444;
    max-width: 700px;
    margin: 0 auto 1.8rem auto;
    line-height: 1.6;
  }

  .play-btn {
    background: linear-gradient(135deg, #6bcf7f 0%, #48bb78 100%);
    color: white;
    font-size: 1.3rem;
    font-weight: bold;
    padding: 1rem 2.8rem;
    border-radius: 40px;
    border: none;
    cursor: pointer;
    box-shadow: 0 8px 25px rgba(107, 207, 127, 0.4);
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
      transform: scale(1.05);
      box-shadow: 0 12px 30px rgba(107, 207, 127, 0.5);
    }
  }
`;

const GamesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
`;

const GameCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 2rem 1.5rem;
  text-align: center;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  border: 2px solid #ffd93d;

  .emoji {
    font-size: 2.5rem;
    margin-bottom: 0.8rem;
  }

  h3 {
    color: #ff6b6b;
    margin: 0 0 0.5rem 0;
    font-size: 1.3rem;
  }

  p {
    color: #555;
    font-size: 0.95rem;
    line-height: 1.5;
    margin: 0;
  }
`;

const gamesList = [
  {
    emoji: '🥚',
    title: 'Egg Catcher',
    desc: 'Move your basket to catch fresh eggs dropping from the roost before they break!'
  },
  {
    emoji: '🛸',
    title: 'Chikin Lander',
    desc: 'Physics-based moon lander featuring aerospace-grade poultry propulsion.'
  },
  {
    emoji: '🃏',
    title: 'Poultry Tarot',
    desc: 'Discover what the barnyard oracle predicts for your fortune, mood, and eggs.'
  },
  {
    emoji: '👾',
    title: 'Cluck Invaders',
    desc: 'Classic space arcade defense with laser-equipped hens protecting the coop.'
  }
];

const WebGamesPage = ({ onGameOpen }) => {
  useEffect(() => {
    document.title = 'Play Fun Chicken Web Games Online | Chikin Lady Arcade';
  }, []);

  return (
    <>
      <PageBanner
        badge="🎮 Fowl Play Arcade"
        title="Chicken Web Games Online"
        subtitle="Step right up and test your skills in the Chikin Lady digital arcade! Catch eggs, set high scores, and enjoy cluckin' awesome browser games."
        primaryText="🕹️ Play Egg Catcher Now"
        onPrimaryClick={onGameOpen}
        secondaryText="📱 Appstore Info"
        secondaryLink="/chikin-lady-appstore"
      />

      <GamesSection>
        <PlayFeaturedCard
          whileHover={{ y: -4 }}
          transition={{ duration: 0.2 }}
        >
          <div className="big-icon">🥚🎯</div>
          <h2>Featured Game: Egg Catcher Live!</h2>
          <p>
            Ready to test your reflexes? Control the basket with your keyboard or touch screen, 
            catch golden eggs for bonus multipliers, and keep the farm happy!
          </p>
          <button className="play-btn" onClick={onGameOpen}>
            🚀 Play Now in Browser
          </button>
        </PlayFeaturedCard>

        <GamesGrid>
          {gamesList.map((g, idx) => (
            <GameCard
              key={idx}
              whileHover={{ y: -5, scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <div className="emoji">{g.emoji}</div>
              <h3>{g.title}</h3>
              <p>{g.desc}</p>
            </GameCard>
          ))}
        </GamesGrid>
      </GamesSection>

      <FeaturedProducts />
    </>
  );
};

export default WebGamesPage;
